import { Vue, Component, Watch } from 'vue-property-decorator';
import moment from 'moment';

import { AuditReportListItemDTO } from '@/../client-generator/generated-clients/quality';
import { SiteCustomerListItemDTO } from '@/../client-generator/generated-clients/sites';

import qualityClient from '@/core/api/quality-service';
import { LOADING_CONTENT } from '@/core/store/types/action-types';
import sitesClient from '@/core/api/sites-service';
import fileDownload from 'js-file-download';

@Component
export default class Reports extends Vue {
  sites: SiteCustomerListItemDTO[] = [];
  siteId = '';
  showReports = false;

  reports: AuditReportListItemDTO[] = [];
  total = 0;
  pageSize = 10;

  fromDate = moment().subtract(1, 'months').toDate();
  toDate = moment().toDate();

  get filters() {
    return [
      {
        value: this.siteId,
        prop: 'siteId',
      },
    ];
  }

  get showSitesPagination() {
    return this.total > this.pageSize;
  }

  get datePickerOptions() {
    return {
      disabledDate: (date) => {
        return date < new Date(this.fromDate);
      },
    };
  }

  @Watch('fromDate')
  fromDateUpdated() {
    this.fetchReports();
  }

  @Watch('toDate')
  toDateUpdated() {
    this.fetchReports();
  }

  mounted() {
    this.fetchSites();
  }

  auditDate(d: string) {
    return moment(d).utcOffset('+0200').format('DD-MM-YYYY').toString();
  }

  async fetchSites() {
    this.$store.dispatch(LOADING_CONTENT, true);
    try {
      const response = await sitesClient.myList(undefined);
      let sites = response.data as SiteCustomerListItemDTO[];
      const site = new SiteCustomerListItemDTO({
        siteCode: '',
        siteName: this.$t('all').toString(),
      });
      sites.push(site);
      sites = sites.reverse();
      this.sites = sites;
      this.showReports = true;
    } catch (error) {
      let messageError = 'error';

      switch (error.status) {
        case 401:
          messageError = this.$t('apiErrors.unauthorized').toString();
          break;
        case 403:
          messageError = this.$t('apiErrors.forbidden').toString();
          break;
        default:
          messageError = this.$t('apiErrors.serverError').toString();
      }

      this.$message.error(messageError);
    } finally {
      this.$store.dispatch(LOADING_CONTENT, false);
    }
  }

  async fetchReports(queryInfo?) {
    if (queryInfo) queryInfo.andFilter = false;

    this.$store.dispatch(LOADING_CONTENT, true);
    try {
      const response = await qualityClient.myList(
        this.fromDate.toISOString(),
        this.toDate.toISOString(),
        JSON.stringify(queryInfo)
      );

      this.reports = response.data as AuditReportListItemDTO[];
      this.total = response.total as number;
    } catch (error) {
      let messageError = 'error';

      switch (error.status) {
        case 401:
          messageError = this.$t('apiErrors.unauthorized').toString();
          break;
        case 403:
          messageError = this.$t('apiErrors.forbidden').toString();
          break;
        default:
          messageError = this.$t('apiErrors.serverError').toString();
      }

      this.$message.error(messageError);
    } finally {
      this.$store.dispatch(LOADING_CONTENT, false);
    }
  }

  async downloadReport(report: AuditReportListItemDTO) {
    if (report.reportId == null || !report.fileName) return;
    this.$store.dispatch(LOADING_CONTENT, true);
    try {
      const response = await qualityClient.downloadReport(report.reportId);
      fileDownload(response.data, report.fileName);
    } catch (error) {
      let messageError = 'error';

      switch (error.status) {
        case 401:
          messageError = this.$t('apiErrors.unauthorized').toString();
          break;
        case 403:
          messageError = this.$t('apiErrors.forbidden').toString();
          break;
        case 404:
          messageError = this.$t('apiErrors.fileNotFound').toString();
          break;
        case 406:
          messageError = this.$t('apiErrors.fileNotAssociatedWithUser').toString();
          break;
        default:
          messageError = this.$t('apiErrors.serverError').toString();
      }

      this.$message.error(messageError);
    } finally {
      this.$store.dispatch(LOADING_CONTENT, false);
    }
  }

  getOutcomeClass(outcome: boolean) {
    if (outcome) {
      return 'text-success';
    }

    return 'text-danger';
  }

  getOutcomeText(outcome: boolean) {
    if (outcome) {
      return this.$t('adequate');
    }

    return this.$t('inadequate');
  }

  getContraText(contradictory: boolean) {
    if (contradictory) {
      return this.$t('yes');
    }

    return this.$t('no');
  }
}
